import React, { useRef, useState } from "react";
import { apiClient } from "@/services/apiClient";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";

interface ImportReviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  /** Called after a successful import so the parent can refetch. */
  onImported: () => void;
}

interface ImportResult {
  created: number;
  skipped: number;
}

/**
 * Import reviews from JSON, pasted or from a file (Home's Reviews panel). A
 * create flow in the shared Drawer (DD-051): the floating right panel on
 * desktop, the bottom sheet on phones. It keeps its modal-era name, so the
 * panel is unchanged.
 */
export const ImportReviewsModal: React.FC<ImportReviewsModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onImported,
}) => {
  const [jsonInput, setJsonInput] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportJson = async () => {
    if (!jsonInput.trim()) {
      setImportError("Please paste JSON data");
      return;
    }

    try {
      setIsImporting(true);
      setImportError(null);
      setImportSuccess(null);

      const parsed = JSON.parse(jsonInput) as unknown;
      const reviewsArray = Array.isArray(parsed) ? parsed : [parsed];

      const result = await apiClient.post<ImportResult>(
        `/client/${clientId}/reviews`,
        reviewsArray
      );
      onImported();
      setJsonInput("");

      const { created, skipped } = result;
      if (created > 0 && skipped > 0) {
        setImportSuccess(`Imported ${created} review${created !== 1 ? "s" : ""}, skipped ${skipped} duplicate${skipped !== 1 ? "s" : ""}`);
      } else if (created > 0) {
        setImportSuccess(`Imported ${created} review${created !== 1 ? "s" : ""}`);
      } else if (skipped > 0) {
        setImportSuccess(`All ${skipped} review${skipped !== 1 ? "s" : ""} already exist`);
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setImportError("Invalid JSON format");
      } else {
        setImportError("Failed to import reviews");
      }
      console.error("Failed to import reviews:", error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setJsonInput(text);
      setImportError(null);
    } catch (error) {
      setImportError("Failed to read file");
      console.error("Failed to read file:", error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Import Reviews"
      bodyPadding
      a11yDescription="Paste reviews as JSON, or upload a JSON file."
      className="md:max-w-lg"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="mr-auto"
          >
            Upload JSON File
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleImportJson()}
            disabled={!jsonInput.trim()}
            isLoading={isImporting}
          >
            Import
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <textarea
          value={jsonInput}
          onChange={(e) => {
            setJsonInput(e.target.value);
            setImportError(null);
            setImportSuccess(null);
          }}
          placeholder='Paste JSON here... e.g., [{"author": "John", "rating": "5.0", "review_text": "Great!", "owner_responded": false}]'
          className="w-full p-3 border border-gray-300 rounded-lg text-sm font-mono resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={6}
        />
        {importError && <p className="text-sm text-red-600">{importError}</p>}
        {importSuccess && <p className="text-sm text-green-600">{importSuccess}</p>}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    </Drawer>
  );
};
